// ListActionsFloatButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  ContainerOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  RestOutlined,
} from '@ant-design/icons';
import { FloatButton } from 'antd';
import { createPortal } from 'react-dom';
import translateX from 'utils/translateX';
import { useModuleStore } from 'stores/module';
import { useListContext } from 'components/core/ListProvider';
import { useModuleContext } from 'components/core/ModuleProvider';
import { PiLifebuoy } from 'react-icons/pi';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ListActionsFloatButton = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentPage = useZustand('module', 'currentPage');
  const { listSettings: storedListSettings } = useModuleStore();

  // For list pages, actions come from table.actions (same as column actions)
  const tableActions = storedCurrentPage?.table?.actions || [];

  const {
    floatButtonCustomActionsFn,
    floatButtonDisabledActionsFn,
    selectedRows = [],
    refresh,
    query,
  } = useListContext();

  const {
    handleEditDocument,
    handlePreviewDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
  } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Get current folder - same logic as buildActionsButtonColumn
  const folder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder;
  const isArchiveOrRecycle = ['archive', 'recycle'].includes(folder);
  const isRecycle = folder === 'recycle';

  // Determine which actions to show based on folder (same logic as buildActionsButtonColumn)
  // DOCUMENTS (inbox): edit, view, archive, remove
  // ARCHIVE: edit, view, restore, remove
  // RECYCLE: edit, view, restore, permanently remove (danger)
  const showEdit = tableActions.includes('edit');
  const showView = tableActions.includes('view');
  const showArchive = !isArchiveOrRecycle && tableActions.includes('archive');
  const showRemove = tableActions.includes('remove');
  const showRestore = isArchiveOrRecycle;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const actionOptions = { onSuccess: refresh };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Check if actions should be disabled based on module-specific logic
  // The function returns true if at least one selected row should disable the action
  const isArchiveDisabled =
    typeof floatButtonDisabledActionsFn === 'function' &&
    floatButtonDisabledActionsFn(selectedRows, 'archive');

  const isRemoveDisabled =
    typeof floatButtonDisabledActionsFn === 'function' &&
    floatButtonDisabledActionsFn(selectedRows, 'remove');

  const isRestoreDisabled =
    typeof floatButtonDisabledActionsFn === 'function' &&
    floatButtonDisabledActionsFn(selectedRows, 'restore');

  // Edit and View are disabled when multiple rows are selected
  const isEditDisabled = selectedRows.length > 1;
  const isViewDisabled = selectedRows.length > 1;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!tableActions?.length) return null;

  return createPortal(
    <FloatButton.Group
      type="primary"
      shape="circle"
      style={{ right: 24, bottom: 64, zIndex: 9999 }}
      trigger="click"
      badge={{ count: selectedRows.length }}
      icon={<MoreOutlined />}
    >
      {typeof floatButtonCustomActionsFn === 'function' && floatButtonCustomActionsFn(selectedRows)}

      {/* RESTORE - Only in archive/recycle folders */}
      {showRestore && (
        <FloatButton
          icon={<PiLifebuoy />}
          tooltip={{
            title: translateX('restore'),
            placement: 'left',
          }}
          onClick={() => handleRestoreDocuments(selectedRows, actionOptions)}
          disabled={isRestoreDisabled}
        />
      )}

      {/* REMOVE / PERMANENTLY REMOVE */}
      {showRemove && (
        <FloatButton
          icon={<RestOutlined style={isRecycle && !isRemoveDisabled ? { color: '#ff4d4f' } : {}} />}
          tooltip={{
            title: translateX(isRecycle ? 'permanently_remove' : 'remove'),
            placement: 'left',
          }}
          onClick={() => handleRemoveDocuments(selectedRows, actionOptions)}
          disabled={isRemoveDisabled}
        />
      )}

      {/* ARCHIVE - Only in documents/inbox folder */}
      {showArchive && (
        <FloatButton
          icon={<ContainerOutlined />}
          tooltip={{
            title: translateX('archive'),
            placement: 'left',
          }}
          onClick={() => handleArchiveDocuments(selectedRows, actionOptions)}
          disabled={isArchiveDisabled}
        />
      )}

      {/* EDIT - Disabled when multiple rows selected */}
      {showEdit && (
        <FloatButton
          icon={<EditOutlined />}
          tooltip={{
            title: translateX('edit'),
            placement: 'left',
          }}
          onClick={() => handleEditDocument(selectedRows[0])}
          disabled={isEditDisabled}
        />
      )}

      {/* VIEW - Disabled when multiple rows selected */}
      {showView && (
        <FloatButton
          icon={<EyeOutlined />}
          tooltip={{
            title: translateX('view'),
            placement: 'left',
          }}
          onClick={() => handlePreviewDocument(selectedRows[0])}
          disabled={isViewDisabled}
        />
      )}
    </FloatButton.Group>,
    document.body
  );
};

export default ListActionsFloatButton;
